
const revealObserver = "IntersectionObserver" in window
  ? new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.12 },
    )
  : null;

function initializeCovamioTheme(root = document) {
  const menuButton = root.matches?.(".menu-toggle") ? root : root.querySelector?.(".menu-toggle");
  const mobileNav = root.matches?.(".mobile-nav") ? root : root.querySelector?.(".mobile-nav");

  if (menuButton && menuButton.dataset.covMenuBound !== "true") {
    menuButton.dataset.covMenuBound = "true";
    menuButton.addEventListener("click", () => {
      const open = menuButton.getAttribute("aria-expanded") === "true";
      menuButton.setAttribute("aria-expanded", String(!open));
      mobileNav?.classList.toggle("is-open", !open);
      document.body.classList.toggle("menu-open", !open);
    });
  }

  root.querySelectorAll?.(".mobile-nav a").forEach((link) => {
    if (link.dataset.covMenuLinkBound === "true") return;
    link.dataset.covMenuLinkBound = "true";
    link.addEventListener("click", () => {
      menuButton?.setAttribute("aria-expanded", "false");
      mobileNav?.classList.remove("is-open");
      document.body.classList.remove("menu-open");
    });
  });

  root.querySelectorAll?.("[data-resource-menu]").forEach((menu) => {
    const toggle = menu.querySelector("[data-resource-toggle]");
    if (!toggle || toggle.dataset.covResourceBound === "true") return;
    toggle.dataset.covResourceBound = "true";
    toggle.addEventListener("click", (event) => {
      event.stopPropagation();
      const willOpen = !menu.classList.contains("is-open");
      document.querySelectorAll("[data-resource-menu].is-open").forEach((openMenu) => {
        openMenu.classList.remove("is-open");
        openMenu.querySelector("[data-resource-toggle]")?.setAttribute("aria-expanded", "false");
      });
      menu.classList.toggle("is-open", willOpen);
      toggle.setAttribute("aria-expanded", String(willOpen));
    });
  });

  root.querySelectorAll?.(".reveal").forEach((element) => {
    if (element.dataset.covRevealBound === "true") return;
    element.dataset.covRevealBound = "true";
    if (revealObserver) revealObserver.observe(element);
    else element.classList.add("is-visible");
  });

  root.querySelectorAll?.("[data-grid-toggle]").forEach((button) => {
    if (button.dataset.covGridBound === "true") return;
    button.dataset.covGridBound = "true";
    button.addEventListener("click", () => {
      document.querySelector("[data-product-grid]")?.classList.toggle("is-dense");
    });
  });

  root.querySelectorAll?.(".routes-visual").forEach((gallery) => {
    if (gallery.dataset.covGalleryBound === "true") return;
    gallery.dataset.covGalleryBound = "true";
    const panels = Array.from(gallery.querySelectorAll(".route-panel"));
    const previous = gallery.querySelector("[data-gallery-previous]");
    const next = gallery.querySelector("[data-gallery-next]");
    let current = 0;
    const select = (index) => {
      if (!panels.length) return;
      current = (index + panels.length) % panels.length;
      panels.forEach((panel, panelIndex) => panel.classList.toggle("is-selected", panelIndex === current));
      panels[current]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    };
    previous?.addEventListener("click", () => select(current - 1));
    next?.addEventListener("click", () => select(current + 1));
    select(0);
  });
}

function initializeCovamioVisualPolish() {
  if (document.documentElement.dataset.covVisualPolishBound === "true") return;
  document.documentElement.dataset.covVisualPolishBound = "true";

  const header = document.querySelector(".site-header");
  const progress = document.createElement("div");
  progress.className = "covamio-scroll-progress";
  progress.setAttribute("aria-hidden", "true");
  progress.innerHTML = "<span></span>";
  document.body.append(progress);

  let scrollFrame = 0;
  const updateVisualState = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const progressValue = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0;
    document.documentElement.style.setProperty("--cov-v43-scroll", `${progressValue}%`);
    header?.classList.toggle("is-scrolled", window.scrollY > 12);
  };
  const scheduleVisualState = () => {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(() => {
      scrollFrame = 0;
      updateVisualState();
    });
  };
  updateVisualState();
  window.addEventListener("scroll", scheduleVisualState, { passive: true });
  window.addEventListener("resize", scheduleVisualState, { passive: true });

  document.querySelectorAll(".reveal").forEach((element, index) => {
    element.style.setProperty("--cov-v43-reveal-delay", `${Math.min(index * 45, 360)}ms`);
  });

  if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

  document.querySelectorAll(".hero-visual, .inner-hero-visual").forEach((visual) => {
    const target = visual.querySelector(".hero-photo, .inner-photo-frame");
    if (!target || target.dataset.covParallaxBound === "true") return;
    target.dataset.covParallaxBound = "true";
    let frame = 0;

    const reset = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      target.style.setProperty("--cov-v43-x", "0px");
      target.style.setProperty("--cov-v43-y", "0px");
    };

    visual.addEventListener("pointermove", (event) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const bounds = visual.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return;
        const x = (event.clientX - bounds.left) / bounds.width - 0.5;
        const y = (event.clientY - bounds.top) / bounds.height - 0.5;
        target.style.setProperty("--cov-v43-x", `${(x * 10).toFixed(2)}px`);
        target.style.setProperty("--cov-v43-y", `${(y * 8).toFixed(2)}px`);
      });
    });
    visual.addEventListener("pointerleave", reset);
  });
}

initializeCovamioTheme(document);
initializeCovamioVisualPolish();

if (document.documentElement.dataset.covResourceDismissBound !== "true") {
  document.documentElement.dataset.covResourceDismissBound = "true";
  document.addEventListener("click", (event) => {
    if (event.target instanceof Element && event.target.closest("[data-resource-menu]")) return;
    document.querySelectorAll("[data-resource-menu].is-open").forEach((menu) => {
      menu.classList.remove("is-open");
      menu.querySelector("[data-resource-toggle]")?.setAttribute("aria-expanded", "false");
    });
  });
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    document.querySelectorAll("[data-resource-menu].is-open").forEach((menu) => {
      menu.classList.remove("is-open");
      menu.querySelector("[data-resource-toggle]")?.setAttribute("aria-expanded", "false");
    });
  });
}

document.addEventListener("shopify:section:load", (event) => {
  if (event.target instanceof Element) initializeCovamioTheme(event.target);
});

document.addEventListener("shopify:block:select", (event) => {
  if (!(event.target instanceof Element)) return;
  const reveal = event.target.matches(".reveal") ? event.target : event.target.querySelector(".reveal");
  reveal?.classList.add("is-visible");
  event.target.scrollIntoView({ behavior: "smooth", block: "nearest" });
});
